import { FormGroup } from '@angular/forms';

import { InfiniteScrollResponse } from '@lib/core/models/infinite-scroll-response.interface';
import {
  DoctorsFilter,
  LocationsFilter,
  OfferingsFilter,
  PartnerAvailabilityFilter,
  PartnerImportStatusFilter,
  PartnerIntegrationTypeFilter,
  PartnerStatusFilter,
  PartnersFilter,
} from '@lib/core/models/partner-filters.interface';
import {
  AppointmentCategory,
  Contact,
  DepartmentSchedule,
  DoctorResponse,
  DoctorSearchModelV2,
  DoctorV2,
  IntegrationType,
  LocationResponse,
  LocationSearchModelV2,
  LocationV2,
  OfferingResponse,
  OfferingSearchModelV2,
  OfferingV2,
  PartnerControls,
  PartnerImportStatus,
  PartnerPayloadV2,
  PartnerResponse,
  PartnerSearchModelV2,
  PartnerUpdateStatus,
  PartnerV2,
  ScheduleUiModel,
  ServiceType,
} from '@lib/core/models/partners.interface';

export const mapInfiniteScrollResponse = <A, B>(res: InfiniteScrollResponse<A>, mapItem: (a: A) => B): InfiniteScrollResponse<B> => ({
  continuationToken: res.continuationToken ?? '',
  items: (res.items ?? []).filter(item => item != null).map(mapItem),
});

// TODO: Handle any logoUrl or photoUrl adjustments logic in this method, if needed.
//       In case of API responding with inconsistent urls.
export const handleAssetUrl = (url: string | null | undefined): string => {
  return url ? url : '';
};

export const toPartnerV2 = (p: PartnerResponse): PartnerV2 => {
  if (!p) {
    console.error('❌ toPartnerV2 received null/undefined partner response');
    throw new Error('Partner response cannot be null or undefined');
  }
  
  return {
    id: p.id ?? '',
    name: p.name ?? '',
    logoUrl: handleAssetUrl(p.logoUrl),
    active: p.active ?? false,
    available: p.available ?? false,
    integrationEnabled: p.integrationEnabled ?? false,
    integration: p.integration ?? IntegrationType.None,
    supportedServices: p.supportedServices ?? [],
    importStatus: p.importStatus ?? PartnerImportStatus.Orphaned,
    updateStatus: p.updateStatus ?? PartnerUpdateStatus.Pristine,
    contacts: p.contacts ?? [],
  };
};

export const toLocationV2 = (l: LocationResponse): LocationV2 => ({
  id: l.id ?? '',
  partnerId: l.partnerId ?? '',
  partnerName: l.partnerName ?? '',
  name: l.name ?? '',
  logoUrl: handleAssetUrl(l.logoUrl),
  active: l.active ?? false,
  available: l.available ?? false,
  integrationEnabled: l.integrationEnabled ?? false,
  integration: l.integration ?? IntegrationType.None,
  supportedServices: l.supportedServices ?? [],
  importStatus: l.importStatus ?? PartnerImportStatus.Orphaned,
  updateStatus: l.updateStatus ?? PartnerUpdateStatus.Pristine,
  contacts: l.contacts ?? [],
  location: l.location ?? { city: '', address: '', coordinates: { latitude: 0, longitude: 0 } },
  schedule: l.schedule ?? [],
});

export const toDoctorV2 = (d: DoctorResponse): DoctorV2 => ({
  id: d.id ?? '',
  partnerId: d.partnerId ?? '',
  fullName: d.fullName ?? '',
  integrationEnabled: d.importStatus === PartnerImportStatus.Synced,
  importStatus: d.importStatus ?? PartnerImportStatus.Orphaned,
  integration: IntegrationType.None,
  active: d.active ?? false,
  available: d.available ?? false,
  updateStatus: d.updateStatus ?? PartnerUpdateStatus.Pristine,
  photoUrl: handleAssetUrl(d.photoUrl),
  specialization: d.specialization ?? '',
  specializations: d.specializations ?? [],
  presentationVideoUrl: d.presentationVideoUrl ?? '',
  isPersonalDoctor: d.isPersonalDoctor ?? false,
  isPopular: d.isPopular ?? false,
});

export const toOfferingV2 = (o: OfferingResponse): OfferingV2 => ({
  $type: o.$type ?? 0,
  id: o.id ?? '',
  partnerId: o.partnerId ?? '',
  serviceType: o.serviceType ?? ServiceType.Generic,
  name: o.name ?? '',
  description: o.description ?? '',
  code: o.code ?? '',
  externalId: o.externalId ?? '',
  externalCode: o.externalCode ?? '',
  price: o.price ?? 0,
  currency: o.currency ?? '',
  integrationEnabled: o.importStatus === PartnerImportStatus.Synced,
  importStatus: o.importStatus ?? PartnerImportStatus.Orphaned,
  integration: o.integration ?? IntegrationType.None,
  active: o.active ?? false,
  available: o.available ?? false,
  updateStatus: o.updateStatus ?? PartnerUpdateStatus.Pristine,
  allowedForGenders: o.allowedForGenders ?? [],
  minAge: o.minAge ?? 0,
  maxAge: o.maxAge ?? 0,
  canBeOrderedOnline: o.canBeOrderedOnline ?? false,
  canProduceMedicalData: o.canProduceMedicalData ?? false,
  appointmentCategory: o.appointmentCategory ?? null,
  alternativeCodes: o.alternativeCodes ?? [],
});

export function evaluatePartnerStatusPayload(status: PartnerStatusFilter): boolean | null {
  switch (status) {
    case PartnerStatusFilter.Active:
      return true;
    case PartnerStatusFilter.Inactive:
      return false;
    default:
      return null;
  }
}

export function evaluatePartnerAvailabilityPayload(availability: PartnerAvailabilityFilter): boolean | null {
  switch (availability) {
    case PartnerAvailabilityFilter.Available:
      return true;
    case PartnerAvailabilityFilter.Unavailable:
      return false;
    default:
      return null;
  }
}

export function evaluatePartnerImportStatusPayload(importStatus: PartnerImportStatusFilter): string | null {
  return importStatus !== PartnerImportStatusFilter.All ? importStatus : null;
}

export function createPartnersSearchPayload(payload: { pageSize: number; filter: PartnersFilter; continuationToken: string }): PartnerSearchModelV2 {
  const search: string = payload.filter.search.trim();
  const status: boolean | null = evaluatePartnerStatusPayload(payload.filter.status);
  const availability: boolean | null = evaluatePartnerAvailabilityPayload(payload.filter.availability);
  const type: string | null = payload.filter.type !== PartnerIntegrationTypeFilter.All ? payload.filter.type : null;
  const importStatus: string | null = evaluatePartnerImportStatusPayload(payload.filter.importStatus);
  const continuationToken: string = payload.continuationToken;

  const searchModel: PartnerSearchModelV2 = {
    pageSize: payload.pageSize,
    searchTerm: search,
  };
  if (status !== null) searchModel.active = status;
  if (availability !== null) searchModel.available = availability;
  if (type) searchModel.integrationEnabled = type === PartnerIntegrationTypeFilter.Integration;
  if (importStatus) searchModel.importStatus = importStatus as PartnerImportStatus;
  if (continuationToken) searchModel.continuationToken = continuationToken;
  return searchModel;
}

export function createLocationsSearchPayload(payload: { partnerId: string; pageSize: number; filter: LocationsFilter; continuationToken: string }): LocationSearchModelV2 {
  const search: string = payload.filter.search.trim();
  const status: boolean | null = evaluatePartnerStatusPayload(payload.filter.status);
  const availability: boolean | null = evaluatePartnerAvailabilityPayload(payload.filter.availability);
  const type: string | null = payload.filter.type !== PartnerIntegrationTypeFilter.All ? payload.filter.type : null;
  const importStatus: string | null = evaluatePartnerImportStatusPayload(payload.filter.importStatus);
  const continuationToken: string = payload.continuationToken;

  const searchModel: LocationSearchModelV2 = {
    partnerId: payload.partnerId,
    pageSize: payload.pageSize,
    searchTerm: search,
  };
  if (status !== null) searchModel.active = status;
  if (availability !== null) searchModel.available = availability;
  if (type) searchModel.integrationEnabled = type === PartnerIntegrationTypeFilter.Integration;
  if (importStatus) searchModel.importStatus = importStatus as PartnerImportStatus;
  if (continuationToken) searchModel.continuationToken = continuationToken;
  return searchModel;
}

export function createDoctorsSearchPayload(payload: { partnerId: string; pageSize: number; filter: DoctorsFilter; continuationToken: string }): DoctorSearchModelV2 {
  const search: string = payload.filter.search.trim();
  const status: boolean | null = evaluatePartnerStatusPayload(payload.filter.status);
  const availability: boolean | null = evaluatePartnerAvailabilityPayload(payload.filter.availability);
  const type: string | null = payload.filter.type !== PartnerIntegrationTypeFilter.All ? payload.filter.type : null;
  const importStatus: string | null = evaluatePartnerImportStatusPayload(payload.filter.importStatus);
  const continuationToken: string = payload.continuationToken;

  const searchModel: DoctorSearchModelV2 = {
    partnerId: payload.partnerId,
    pageSize: payload.pageSize,
    searchTerm: search,
  };
  if (status !== null) searchModel.active = status;
  if (availability !== null) searchModel.available = availability;
  if (type) searchModel.integrationEnabled = type === PartnerIntegrationTypeFilter.Integration;
  if (importStatus) searchModel.importStatus = importStatus as PartnerImportStatus;
  if (continuationToken) searchModel.continuationToken = continuationToken;
  return searchModel;
}

export function createOfferingsSearchPayload(payload: { partnerId: string; pageSize: number; filter: OfferingsFilter; continuationToken: string }): OfferingSearchModelV2 {
  const search: string = payload.filter.search.trim();
  const status: boolean | null = evaluatePartnerStatusPayload(payload.filter.status);
  const availability: boolean | null = evaluatePartnerAvailabilityPayload(payload.filter.availability);
  const type: string | null = payload.filter.type !== PartnerIntegrationTypeFilter.All ? payload.filter.type : null;
  const importStatus: string | null = evaluatePartnerImportStatusPayload(payload.filter.importStatus);
  const mappedOnly: boolean | null = payload.filter.mappedOnly;
  const continuationToken: string = payload.continuationToken;

  const searchModel: OfferingSearchModelV2 = {
    partnerId: payload.partnerId,
    pageSize: payload.pageSize,
    searchTerm: search,
  };
  if (status !== null) searchModel.active = status;
  if (availability !== null) searchModel.available = availability;
  if (type) searchModel.integrationEnabled = type === PartnerIntegrationTypeFilter.Integration;
  if (importStatus) searchModel.importStatus = importStatus as PartnerImportStatus;
  if (mappedOnly !== null) searchModel.mappedOnly = mappedOnly;
  if (continuationToken) searchModel.continuationToken = continuationToken;
  return searchModel;
}

export function createPartnerInfoPayload(partnerForm: FormGroup<PartnerControls>): PartnerPayloadV2 {
  const formValue = partnerForm.getRawValue();

  const payload: PartnerPayloadV2 = {
    id: <string>formValue.id ?? null,
    integrationEnabled: <boolean>formValue.integrationEnabled,
    integration: formValue.integration ?? IntegrationType.None,
    supportedServices: formValue.supportedServices ?? [],
    name: <string>formValue.name,
    active: <boolean>formValue.active,
    available: <boolean>formValue.available,
    contacts: <Partial<Contact>[]>formValue.contacts,
  };
  if (!payload.id) {
    delete payload.id;
  }
  return payload;
}

export function groupSchedulesByTime(schedules: DepartmentSchedule[]): ScheduleUiModel[] {
  const groupedArray: ScheduleUiModel[] = [];

  schedules?.map(schedule => {
    const existingGroup = groupedArray.find(group => group.formattedHours === schedule.formattedHours);
    const [opening, closing] = schedule.formattedHours ? schedule.formattedHours.split(' - ') : [schedule.from, schedule.to];

    return !existingGroup
      ? groupedArray.push({
          daysOfWeek: [schedule.dayOfWeek],
          from: opening,
          to: closing,
          formattedHours: `${opening} - ${closing}`,
        })
      : existingGroup.daysOfWeek.push(schedule.dayOfWeek);
  });

  return groupedArray ?? [];
}

export function flattenSchedulesToDepartmentSchedule(schedules: Partial<ScheduleUiModel>[]): Partial<DepartmentSchedule>[] {
  return schedules.flatMap(schedule =>
    (schedule.daysOfWeek ?? []).map(day => ({
      dayOfWeek: day,
      from: schedule.from,
      to: schedule.to,
      formattedHours: schedule.formattedHours,
    }))
  );
}
